import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateClipCaches } from './use-clips';
import {
  listClipTags,
  createClipTag,
  deleteClipTag,
  type ListClipTagsParams,
} from '../services/clip-tags.service';

// The managed tag vocabulary for the caller's federation + sport. Keyed by scope so the super_admin
// console (explicit ids) and a member's own view don't share a cache entry. Enabled always for a
// member (empty params → own scope); the console passes ids. The vocabulary changes rarely, so it
// rides the app's default long stale window.
export function useClipTagsQuery(params: ListClipTagsParams = {}, enabled = true) {
  return useQuery({
    queryKey: ['clip-tags', { federationId: params.federationId ?? null, sportId: params.sportId ?? null }],
    queryFn: () => listClipTags(params),
    enabled,
  });
}

// Add a label to the vocabulary. Invalidates every clip-tags list so the new tag appears in the
// editor picker + the referee filter without a manual refetch.
export function useCreateClipTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createClipTag,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clip-tags'] });
    },
  });
}

// Remove a label. Invalidates the tag lists AND every clip query — a deleted tag cascades off any
// clip that carried it, so every cached clip payload is now stale. Routed through the shared
// invalidateClipCaches rather than a hand-listed subset: the hand-listed version here missed
// ['clip', id] (the open editor) and ['daily-clip'] (the Home hero), which would have kept
// rendering a tag that no longer exists.
export function useDeleteClipTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteClipTag,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clip-tags'] });
      invalidateClipCaches(qc);
    },
  });
}
